import { createFileRoute } from "@tanstack/react-router";
import { Phone, Star, ShieldCheck, Clock } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { Reveal } from "../components/reveal";
import { HomeReviews } from "../components/home-reviews";
import { InlineQuoteSection } from "@/components/inline-quote-section";
import { QuickLeadForm } from "@/components/quick-lead-form";
import { InternalLinkRow, POPULAR_SERVICE_LINKS, CORE_CITY_LINKS } from "@/components/internal-links";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Reviews | Capital Pro Pressure Washing Albany NY";
const DESC =
  "Read Google reviews for Capital Pro Pressure Washing — 5-star rated house washing, roof washing and concrete cleaning across Albany, Troy, Schenectady and Saratoga Springs, NY.";

const POINTS = [
  {
    icon: Clock,
    title: "We Show Up When We Say",
    desc: "The single thing customers mention most: confirmed windows, a text when we're on the way, and a crew that arrives on schedule.",
  },
  {
    icon: ShieldCheck,
    title: "Soft Wash, No Damage",
    desc: "Low-pressure cleaning on siding and roofs, plants rinsed and protected, and a walk-through before we leave the property.",
  },
  {
    icon: Star,
    title: "Owner-Run, Not A Call Center",
    desc: "Luis quotes the job and is on-site for it. That's why reviews name the crew by name instead of just the company.",
  },
];

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/reviews` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/reviews` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Capital Pro Pressure Washing",
          url: `${SITE}/reviews`,
          telephone: "+15189001913",
          priceRange: "$$",
          image: `${SITE}/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg`,
          address: {
            "@type": "PostalAddress",
            streetAddress: "5A Vatrano Rd",
            addressLocality: "Albany",
            addressRegion: "NY",
            postalCode: "12205",
            addressCountry: "US",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: "90",
            bestRating: "5",
          },
        }),
      },
    ],
  }),
  component: ReviewsPage,
});

const DISPLAY = { fontFamily: "var(--font-display)", fontWeight: 700 } as const;
const H2 = "text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight";

function ReviewsPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Reviews" }]} />

      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Rated 5 Stars On Google
          </p>
          <h1 className="text-3xl md:text-5xl uppercase leading-tight" style={DISPLAY}>
            Capital Pro Pressure Washing Reviews
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Real, verified Google reviews from homeowners in Albany, Colonie, Troy, Schenectady,
            Clifton Park, Delmar and Saratoga Springs. They pull live from our Google Business
            Profile, so what you read here is what's posted publicly.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              Get My Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-8 md:py-10">
        <div className="mx-auto max-w-[760px] px-4">
          <QuickLeadForm source="reviews-top" />
        </div>
      </section>

      <HomeReviews />

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-[1100px] px-4">
          <h2 className={`${H2} text-center mb-8`} style={DISPLAY}>What Customers Keep Mentioning</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {POINTS.map((p, i) => (
              <Reveal key={p.title} delayMs={i * 60} className="card-pop">
                <div className="h-full rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6">
                  <p.icon size={22} className="text-[var(--brand-red)]" />
                  <h3 className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase" style={DISPLAY}>
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[var(--brand-charcoal)]/75 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center text-[var(--brand-charcoal)]/75 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
            Want to see the work behind the reviews? Browse the{" "}
            <a href="/gallery" className="text-[var(--brand-bright-blue)] font-semibold hover:text-[var(--brand-deep-blue)]">
              before &amp; after gallery
            </a>
            , check{" "}
            <a href="/pricing" className="text-[var(--brand-bright-blue)] font-semibold hover:text-[var(--brand-deep-blue)]">
              typical pricing
            </a>
            , or{" "}
            <a href="/contact" className="text-[var(--brand-bright-blue)] font-semibold hover:text-[var(--brand-deep-blue)]">
              get in touch
            </a>
            .
          </p>
        </div>
      </section>

      <InternalLinkRow title="Popular Services" links={POPULAR_SERVICE_LINKS} />
      <InternalLinkRow title="Areas We Serve" links={CORE_CITY_LINKS} tone="white" />

      <InlineQuoteSection />
    </main>
  );
}
